/**
 * Custom Dashboard Registry
 *
 * Each entry requires customerId (user.customerId) to match before any tabs
 * are shown. The `tabs` array defines one or more custom tabs for that customer.
 *
 * HOW TO ADD A NEW CUSTOMER DASHBOARD:
 *  1. Create the component at:
 *       src/components/customDashboards/<CustomerName>Dashboard.jsx
 *  2. Add an entry to the array below with a `tabs` array.
 *     - customerId → value of idCustomers in the customers table
 *     - clientId   → value of idClients in the customers table
 *
 * TAB PROPERTIES:
 *   - label              : Tab button text (required)
 *   - icon               : FontAwesome icon object (optional, falls back to faTableColumns)
 *   - requiredPermission : Permission module string (optional; portal users only)
 *   - component          : lazy-loaded React component (required)
 *
 * Each component receives these props automatically:
 *   { user, tenant, getTenantLink, navigate }
 */

import { lazy } from 'react';
import { faTableColumns } from '@fortawesome/free-solid-svg-icons';

const customDashboards = [
  // ── Register customer dashboards here ───────────────────────────────────────

  {
    customerId: '7003',  // user.customerId from JWT / session
    clientId:   1,       // user.idClients from JWT (loose match: '1' == 1)
    tabs: [
      {
        label:              'My Dashboard',
        icon:               faTableColumns,
        requiredPermission: 'inventory',
        component:          lazy(() => import('../components/customDashboards/Customer4770Dashboard')),
      },
      // Add more tabs for this customer here, e.g.:
      // {
      //   label:     'Product Detail',
      //   icon:      faBoxesStacked,
      //   component: lazy(() => import('../components/customDashboards/Customer4770ProductDetail')),
      // },
    ],
  },

  {
    customerId: '12888', // customer 12888 / client 5
    clientId:   5,
    tabs: [
      {
        label:              'My Dashboard',
        icon:               faTableColumns,
        requiredPermission: 'inventory',
        component:          lazy(() => import('../components/customDashboards/Customer4770Dashboard')),
      },
    ],
  },

  {
    customerId: '10634', // customer 10634
    clientId:   5,
    tabs: [
      {
        label:              'My Dashboard',
        icon:               faTableColumns,
        requiredPermission: 'inventory',
        component:          lazy(() => import('../components/customDashboards/Customer4770Dashboard')),
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────────────────
];

/**
 * Resolves the list of visible custom tabs for the given user.
 *
 * - customerId must match an entry in the registry.
 * - Base admin users (customers table, isPortalUser=false) always see all tabs.
 * - Portal / custadd users only see tabs whose `requiredPermission` (if set)
 *   is satisfied via the `hasPermission` function from AuthContext.
 *
 * @param {{ customerId?: any, idClients?: any, idClient?: any, isPortalUser?: boolean }} user
 * @param {(module: string) => boolean} [hasPermission]
 * @returns {Array<{ label: string, icon?: object, component: React.LazyExoticComponent }>|null}
 */
export function resolveCustomDashboard(user, hasPermission) {
  if (!user?.customerId) return null;

  const entry = customDashboards.find(
    // customerId is the primary key — numeric comparison avoids cross-match
    // between customers that happen to share the same idClients value.
    (e) => Number(e.customerId) === Number(user.customerId)
  );
  if (!entry?.tabs?.length) return null;

  // Base admin users always get all tabs — no permission check.
  if (!user.isPortalUser) return entry.tabs;

  // Portal / custadd users: filter out tabs whose requiredPermission is unmet.
  const visibleTabs = entry.tabs.filter((tab) => {
    if (!tab.requiredPermission) return true;
    if (typeof hasPermission !== 'function') return false;
    return hasPermission(tab.requiredPermission);
  });

  return visibleTabs.length ? visibleTabs : null;
}

export default customDashboards;
